/**
 * Per-connector MCP setup metadata: shared by the web dialog and the native
 * sheet. The step text + intro live in i18n (`connectors.setup.<slug>.*`); the
 * code snippet (command / mcp.json) and docs link live here since they're
 * technical and not translated.
 *
 * A connector has a setup spec iff it's an AI-rail (MCP) client.
 */
export interface ConnectorSetupSpec {
  /** Renders the copy-paste command / config. `token` is the placeholder when none exists yet. */
  code?: (url: string, token: string) => string;
  /** How the code block is labelled: a shell command vs a JSON config. */
  codeKind?: 'command' | 'config';
  /** Official setup guide. */
  docUrl?: string;
}

/** Shown in the code block until the user generates a real token. */
export const TOKEN_PLACEHOLDER = 'YOUR_TABBIO_TOKEN';

/** Consent-card permission rows: i18n keys under `connectors.consent.*`. */
export const CONSENT_CAN_KEYS = [
  'canEditCv',
  'canReadCv',
  'runYourSkills',
  'canTailorCv',
  'canFindJobs',
  'canDownloadResume',
] as const;

export const CONSENT_CANNOT_KEYS = ['cannotAccessAccount'] as const;

const mcpJson = (url: string, token: string) =>
  JSON.stringify(
    { mcpServers: { tabbio: { url, headers: { Authorization: `Bearer ${token}` } } } },
    null,
    2,
  );

const geminiMcpJson = (url: string, token: string) =>
  JSON.stringify(
    {
      mcpServers: {
        tabbio: {
          httpUrl: url,
          headers: { Authorization: `Bearer ${token}` },
        },
      },
    },
    null,
    2,
  );

export const CONNECTOR_SETUP: Record<string, ConnectorSetupSpec> = {
  chatgpt: {
    docUrl: 'https://developers.openai.com/apps-sdk/deploy/connect-chatgpt',
  },
  claude: {
    docUrl:
      'https://support.claude.com/en/articles/11175166-get-started-with-custom-connectors-using-remote-mcp',
  },
  gemini: {
    codeKind: 'config',
    code: geminiMcpJson,
    docUrl: 'https://github.com/google-gemini/gemini-cli/blob/main/docs/tools/mcp-server.md',
  },
  grok: {
    docUrl: 'https://docs.x.ai/grok/connectors',
  },
  perplexity: {
    codeKind: 'config',
    code: mcpJson,
    docUrl:
      'https://www.perplexity.ai/help-center/en/articles/11502712-local-and-remote-mcps-for-perplexity',
  },
};

export function connectorSetupSpec(slug: string): ConnectorSetupSpec | undefined {
  return CONNECTOR_SETUP[slug];
}
